import React from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { formatDateToISO, getSlotHHMM, formatTimeSlot, formatSlotToISO, getDayName, getDayNumber, isSlotInFuture } from '../../utils/helpers';

interface TableViewProps {
  visibleDates: Date[];
  daysToShow: number;
  slotsByDate?: Record<string, Record<string, string[]>>;
  selectedSlot: string | null;
  onSlotSelect: (slotId: string) => void;
  onOperatoriesSelect?: (operatories: string[]) => void;
  bufferMinutes?: number;
}

const TableView: React.FC<TableViewProps> = ({
  visibleDates,
  daysToShow,
  slotsByDate,
  selectedSlot,
  onSlotSelect,
  onOperatoriesSelect,
  bufferMinutes = 0,
}) => {
  const practice = useSelector((s: RootState) => s.auth.practice);
  const timeZone = practice?.time_zone;
  return (
    <>
      {/* Sticky Date Headers */}
      <div className={`grid gap-2 sticky top-0 bg-white z-10 pb-2 ${daysToShow === 3 ? 'grid-cols-3' : 'grid-cols-7'}`}>
        {visibleDates.map((date, index) => {
          return (
            <div key={index} className="text-center p-2 bg-black rounded-lg">
              <div className="text-xs font-medium text-white">
                {getDayName(date)}
              </div>
              <div className="text-sm font-bold text-white">
                {getDayNumber(date)}
              </div>
            </div>
          );
        })}
      </div>

      {/* Time Slots Grid with Single Scroll */}
      <div className="max-h-72 overflow-y-auto">
        <div className={`grid gap-2 ${daysToShow === 3 ? 'grid-cols-3' : 'grid-cols-7'}`}>
          {visibleDates.map((date, index) => {
            const dateStr = formatDateToISO(date);
            const timeSlotsObj = slotsByDate?.[dateStr] || {};
            const timeSlots = Object.keys(timeSlotsObj)
              .filter((slot) => isSlotInFuture(dateStr, slot, bufferMinutes, timeZone))
              .sort((a, b) => {
                const [ah, am] = getSlotHHMM(a).split(':').map((n) => parseInt(n || '0', 10));
                const [bh, bm] = getSlotHHMM(b).split(':').map((n) => parseInt(n || '0', 10));
                return ah * 60 + am - (bh * 60 + bm);
              });

            return (
              <div key={index} className="space-y-1">
                {timeSlots.length === 0 ? (
                  <div className="text-center py-2 text-gray-400 text-xs">
                    Closed / No Slots Available
                  </div>
                ) : (
                  timeSlots.map((slot: string) => {
                    const slotId = formatSlotToISO(dateStr, slot);
                    const isSelected = selectedSlot === slotId;
                    const operatories_available = timeSlotsObj[slot];

                    return (
                      <button
                        key={slot}
                        onClick={() => {
                          onSlotSelect(slotId);
                          if (onOperatoriesSelect) onOperatoriesSelect(operatories_available || []);
                        }}
                        className={`w-full px-2 py-3 text-xs rounded border transition-all duration-200 ${isSelected
                          ? 'bg-teamcare-yellow border-teamcare-yellow text-black font-medium shadow-md'
                          : 'bg-white border-gray-200 text-gray-700 hover:border-teamcare-yellow hover:bg-teamcare-yellow/10'
                          }`}
                      >
                        {formatTimeSlot(slot)}
                      </button>
                    );
                  })
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default TableView;
